import { Route } from "@/routes/(auth)/(headless)/reports/clients/$id.proposal.$proposalId";
import { useGoal } from "creso-core/hooks";
import { ReportFooter } from "../components/report-footer";
import { ReportHeader } from "../components/report-header";
import { ReportContainer } from "../components/report-container";
import { CoverSection } from "../components/proposal/cover-section";
import { GoalOverviewSection } from "../components/proposal/goal-overview-section";
import { RecommendationsSection } from "../components/proposal/recommendations-section";
import { ExposureSection } from "../components/proposal/exposure-section";
import {
  AssetAllocationReport,
  mapAllocationBreakdownBuckets,
} from "../components/asset-allocation-report";
import { ReportSection } from "../components/proposal/report-section";
import { BacktestSection } from "../components/proposal/backtest-section";
import { SipJourneySection } from "../components/proposal/sip-journey-section";
import { Report } from "../components";

export function ProposalReport() {
  const {
    client,
    distributorProfile,
    distributorBranding,
    employee,
    proposal,
    reportData,
  } = Route.useLoaderData();

  const { embed, sections, backtest_period, backtest_benchmark } =
    Route.useSearch();

  const hasSection = (id: string) => {
    if (!sections) return true;
    return sections.includes(id);
  };

  const isGoalLinked = Boolean(proposal.goal);

  const { data: goalData, isLoading } = useGoal(
    { id: proposal.goal as number },
    { enabled: !!proposal.goal }
  );

  if (isLoading) return null;

  return (
    <Report embed={!!embed}>
      <style>{`
        @page {
          size: B4 landscape !important;
          margin: 0;
        }
      `}</style>
      <ReportHeader
        distributorBranding={distributorBranding}
        profile={distributorProfile}
      />
      <ReportContainer>
        <div className="content px-8 w-full space-y-10">
          <ReportSection sectionId="cover" visible={hasSection("cover")}>
            <CoverSection
              client={client}
              proposal={proposal}
              distributorProfile={distributorProfile}
              employee={employee}
            />
          </ReportSection>

          <ReportSection
            sectionId="goal-overview"
            visible={
              hasSection("goal-overview") &&
              (isGoalLinked || !!proposal.projected_value)
            }
          >
            <GoalOverviewSection
              proposal={proposal}
              goalData={goalData}
              isGoalLinked={isGoalLinked}
            />
          </ReportSection>

          <ReportSection
            sectionId="sip-journey"
            visible={
              hasSection("sip-journey") &&
              (isGoalLinked || !!proposal.projected_value) &&
              Number(proposal.total_sip_amount) > 0
            }
          >
            <SipJourneySection proposal={proposal} />
          </ReportSection>

          <RecommendationsSection
            proposal={proposal}
            showSip={hasSection("sip-recommendations")}
            showLumpsum={hasSection("lumpsum-recommendations")}
          />

          <ReportSection
            sectionId="sip-asset-allocation"
            className="break-before-page"
            visible={
              hasSection("sip-asset-allocation") &&
              Number(proposal.total_sip_amount) > 0
            }
          >
            <div className="flex break-inside-avoid flex-col gap-4">
              <span className="text-lg font-semibold">
                Asset Allocation - SIP
              </span>
              <AssetAllocationReport
                buckets={mapAllocationBreakdownBuckets(
                  reportData?.sip_allocation_breakdown?.buckets
                )}
              />
            </div>
          </ReportSection>

          {Number(proposal.total_sip_amount) > 0 && (
            <div className="flex gap-6">
              <ReportSection
                sectionId="sip-sector-exposure"
                visible={hasSection("sip-sector-exposure")}
                className="flex-1 flex"
              >
                <ExposureSection
                  variant="sip"
                  topSectors={reportData?.sip_top_sectors ?? []}
                  topStocks={reportData?.sip_top_stocks ?? []}
                  show="sectors"
                />
              </ReportSection>

              <ReportSection
                sectionId="sip-stock-exposure"
                visible={hasSection("sip-stock-exposure")}
                className="flex-1 flex"
              >
                <ExposureSection
                  variant="sip"
                  topSectors={reportData?.sip_top_sectors ?? []}
                  topStocks={reportData?.sip_top_stocks ?? []}
                  show="stocks"
                />
              </ReportSection>
            </div>
          )}

          <ReportSection
            sectionId="lumpsum-asset-allocation"
            className="break-before-page"
            visible={
              hasSection("lumpsum-asset-allocation") &&
              Number(proposal.total_lumpsum_amount) > 0
            }
          >
            <div className="flex break-inside-avoid flex-col gap-4">
              <span className="text-lg font-semibold">
                Asset Allocation - One time
              </span>
              <AssetAllocationReport
                buckets={mapAllocationBreakdownBuckets(
                  reportData?.lumpsum_allocation_breakdown?.buckets
                )}
              />
            </div>
          </ReportSection>

          {Number(proposal.total_lumpsum_amount) > 0 && (
            <div className="flex gap-6">
              <ReportSection
                sectionId="lumpsum-sector-exposure"
                visible={hasSection("lumpsum-sector-exposure")}
                className="flex-1 flex"
              >
                <ExposureSection
                  variant="lumpsum"
                  topSectors={reportData?.lumpsum_top_sectors ?? []}
                  topStocks={reportData?.lumpsum_top_stocks ?? []}
                  show="sectors"
                />
              </ReportSection>

              <ReportSection
                sectionId="lumpsum-stock-exposure"
                visible={hasSection("lumpsum-stock-exposure")}
                className="flex-1 flex"
              >
                <ExposureSection
                  variant="lumpsum"
                  topSectors={reportData?.lumpsum_top_sectors ?? []}
                  topStocks={reportData?.lumpsum_top_stocks ?? []}
                  show="stocks"
                />
              </ReportSection>
            </div>
          )}

          <ReportSection
            sectionId="backtest"
            visible={
              hasSection("backtest") &&
              (Number(proposal.total_lumpsum_amount) > 0 ||
                Number(proposal.total_sip_amount) > 0)
            }
          >
            <BacktestSection
              proposal={proposal}
              period={backtest_period}
              benchmark={backtest_benchmark}
              embed={!!embed}
            />
          </ReportSection>
        </div>
      </ReportContainer>
      <ReportFooter>
        <div className="flex flex-col gap-1">
          <span className="text-xxs font-semibold">Disclaimer</span>
          <span className="text-muted-foreground text-xxs">
            Mutual Fund investments are subject to market risk. Please read the
            offer document carefully before investing. The past performance of
            the mutual funds is not necessarily indicative of future performance
            of the schemes.
          </span>
        </div>
      </ReportFooter>
    </Report>
  );
}
